import moment from 'moment';
import {LeanDocument} from 'mongoose';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {ParentHandlerApi} from '@/classes/api/parent-handler.api.class';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {CourseDocument} from '@/documentTypes/course-manager/Course.document';
import {Student} from '@/interfaces/student.interface';
import {PickerItem, PickerLine, PickerTag} from './entityPicker.types';
import {formatMinutes, idOf, WEEKDAY_SHORT} from './entityPicker.helper';
import {bookLabel, loadBookPositions, loadInventories, ownedBooks} from './studentBooks.helper';

type CourseLite = Pick<LeanDocument<CourseDocument>, '_id' | 'customId' | 'dayOfWeek' | 'timeRange' | 'studentItems'>;

/** Students without enrichment beyond this (search results are not paginated) */
export const MAX_ENRICHED_STUDENTS = 25;

/** Participation of a student in a course that has not ended (or was resumed) */
export function isCurrentlyInCourse(course: Pick<CourseLite, 'studentItems'>, studentId: string, now = moment()): boolean {
    return (course.studentItems || []).some((item) => {
        if (idOf(item.student) !== studentId) {
            return false;
        }
        if (!item.endOfParticipation || moment(item.endOfParticipation).isAfter(now)) {
            return true;
        }
        return !!item.resumptionOfParticipation && moment(item.resumptionOfParticipation).isSameOrBefore(now);
    });
}

async function safe<T>(promise: Promise<T>): Promise<T | null> {
    try {
        return await promise;
    } catch (e) {
        return null;
    }
}

function ageOf(birthday: Date | string | undefined): number | null {
    if (!birthday) {
        return null;
    }
    const date = moment(birthday);
    if (!date.isValid()) {
        return null;
    }
    const years = moment().diff(date, 'years');
    return years >= 0 && years < 30 ? years : null;
}

function classOf(student: Student): string {
    const grade = student.class?.grade;
    if (grade === null || grade === undefined) {
        return '';
    }
    return `Klasse ${grade}${student.class?.gradeClassifier || ''}`;
}

function courseLabel(course: CourseLite): string {
    const day = typeof course.dayOfWeek === 'number' ? WEEKDAY_SHORT[course.dayOfWeek] : '';
    const time = course.timeRange ? formatMinutes(course.timeRange.startTime) : '';
    const when = [day, time].filter(Boolean).join(' ');
    return when ? `${course.customId} (${when})` : course.customId;
}

interface StudentContextData {
    parents: Map<string, string> | null;
    institutions: Map<string, string> | null;
    courses: CourseLite[] | null;
    inventories: Awaited<ReturnType<typeof loadInventories>>;
    positions: Awaited<ReturnType<typeof loadBookPositions>>;
}

export interface StudentContextOptions {
    /** Read access on product orders (refProfile + items) – the inventory endpoint requires it */
    canReadInventory: boolean;
}

async function loadStudentContext(students: Student[], options: StudentContextOptions): Promise<StudentContextData> {
    const ids = students.map((s) => String(s._id));
    const parentIds = Array.from(new Set(students.flatMap((s) => ((s.parents || []) as unknown[]).map(idOf))));
    const institutionIds = Array.from(new Set(students.map((s) => s.institution).filter(Boolean).map(idOf)));

    const [parentResp, institutionResp, courseResp, inventories, positions] = await Promise.all([
        parentIds.length ? safe(ParentHandlerApi.find({filter: {_id: parentIds}, fields: ['firstName', 'lastName'], skipPagination: true} as any)) : null,
        institutionIds.length ? safe(InstitutionerApi.find({filter: {_id: institutionIds}, fields: ['name'], skipPagination: true} as any)) : null,
        safe(CourseManagerAPI.find({
            filter: {student: ids, endsAfter: new Date(), courseState: [CourseStates.ACTIVE]},
            fields: ['customId', 'dayOfWeek', 'timeRange', 'studentItems'],
            skipPagination: true,
        } as any)),
        options.canReadInventory ? loadInventories(ids) : null,
        options.canReadInventory ? loadBookPositions() : null,
    ]);
    const parents = parentResp ? new Map(parentResp.parents.map((p) => [String(p._id), `${p.firstName || ''} ${p.lastName || ''}`.trim()])) : null;
    const institutions = institutionResp ? new Map(institutionResp.institutions.map((i) => [String(i._id), i.name])) : null;
    return {parents, institutions, courses: courseResp ? courseResp.courses as CourseLite[] : null, inventories, positions};
}

function studentLines(student: Student, data: StudentContextData): PickerLine[] {
    const id = String(student._id);
    const lines: PickerLine[] = [];
    if (data.parents) {
        const names = ((student.parents || []) as unknown[]).map((p) => data.parents?.get(idOf(p))).filter(Boolean);
        if (names.length) {
            lines.push({icon: 'mdi-account-group-outline', text: names.join(', '), title: 'Eltern'});
        }
    }
    if (data.courses) {
        const current = data.courses.filter((course) => isCurrentlyInCourse(course, id));
        lines.push({
            icon: 'mdi-calendar-clock',
            text: current.length ? current.map(courseLabel).join(', ') : 'keine aktuellen Kurse',
            title: 'Aktuelle Kurse',
        });
    }
    if (data.inventories) {
        const books = ownedBooks(data.inventories.get(id) || [], data.positions);
        if (books.length) {
            lines.push({
                icon: 'mdi-book-open-variant',
                text: books.map(bookLabel).join(', '),
                title: data.positions ? 'Bücher (höchstes zuerst)' : 'Artikel im Besitz',
                tone: 'info',
            });
        }
    }
    return lines;
}

/**
 * Adds age/class, institution, parents, current courses and owned books (highest first) to student items.
 * All facts are loaded in one batch per list; anything without read access is left out.
 */
export async function enrichStudentItems(items: PickerItem[], options: StudentContextOptions): Promise<PickerItem[]> {
    const head = items.slice(0, MAX_ENRICHED_STUDENTS);
    const students = head.map((item) => item.source as Student | undefined).filter((s): s is Student => !!s);
    if (!students.length) {
        return items;
    }
    const data = await loadStudentContext(students, options);
    const enriched = head.map((item) => {
        const student = item.source as Student | undefined;
        if (!student) {
            return item;
        }
        const age = ageOf(student.birthday);
        const institution = student.institution ? data.institutions?.get(idOf(student.institution)) : undefined;
        const subtitle = [age !== null ? `${age} J.` : '', classOf(student), institution || ''].filter(Boolean).join(' · ');
        return {
            ...item,
            subtitle: subtitle || item.subtitle,
            lines: [...(item.lines || []), ...studentLines(student, data)],
        };
    });
    return [...enriched, ...items.slice(MAX_ENRICHED_STUDENTS)];
}

/** Status tags available without extra requests */
export function studentTags(student: Student): PickerTag[] {
    return student.isActive === false ? [{text: 'inaktiv', tone: 'muted', icon: 'mdi-account-off'}] : [];
}
